/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';

import {
  BuilderUserContext,
} from '@gh/builder-shared';

import {
  AbstractSnapshotFormComponent,
  FormGroupSchemaConfig,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  NumberTypes,
  updateSchemaWithChildren,
  whenAuOrNzRegion,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import {
  DialogService,
  getControlValue,
  setControlValue,
  TinymceSettings,
  tmSettingsBuilder,
} from '@gh/core-ui';
import { renderSafeHtml } from '@gh/core-util';

import { find } from 'lodash';
import {
  DrawDetailsPaymentProgressInfo,
  DrawDetailsPaymentScheduleItem,
} from '../../../resources/draw-details.model';
import {
  UserType,
} from '@gh/builder-shared-data';
import { Validators } from '@angular/forms';

@Component({
  selector: 'gh-draw-details-payment-progress-form',
  template: require('./draw-details-payment-progress-form.component.html'),
  styles: [require('./draw-details-payment-progress-form.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawDetailsPaymentProgressFormComponent extends AbstractSnapshotFormComponent<DrawDetailsPaymentProgressInfo> implements OnInit {
  @Input() drawRuleCode: string;
  @Input() drawRuleName: string;
  @Input() schedule: DrawDetailsPaymentScheduleItem[];
  @Input() stageNumber: boolean;
  @Input() lastStageNumber: boolean;
  @Input() quoteVariationExist: boolean;
  @Input() hasSelectedChangeOrders: boolean;
  // tslint:disable-next-line:no-input-prefix
  @Input() isCreateMode: boolean;

  @ViewChild('history', {static: true}) history: ElementRef;

  tmSettings: TinymceSettings;
  private _isCorrectionEnabled = false;

  constructor(private dialogs: DialogService,
              private formStateBuilder: FormStateBuilder,
              private userContext: BuilderUserContext) {
    super();
  }

  get depositAmount(): Maybe<number> {
    const depositStage = find(this.schedule, { isDeposit: true });
    return depositStage ? depositStage.amountDue : void 0;
  }

  get isLastStage(): boolean {
    return this.stageNumber === this.lastStageNumber;
  }

  get historyHtmlContent(): string {
    const { history } = this;
    return history ? renderSafeHtml(history.nativeElement) : '';
  }

  get isOfficeUser(): boolean {
    return (this.userContext.userType !== UserType.Corporate) && (this.userContext.userType !== UserType.Master);
  }

  get isCorrectionEnabled(): boolean {
    return this.form.editMode;
  }

  setIsCorrectionEnabled(value: boolean): void {
    this._isCorrectionEnabled = value;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.tmSettings = tmSettingsBuilder().styles('body { overflow-wrap: break-word; }').scayt().build();
  }

  onChangeCorrection(type: string): void {
    const increment = 0.01;
    const maxCorrection = 0.1;
    const value = getControlValue(this.form.group, 'correction');
    const currentCorrection = parseFloat(value.toFixed(2));
    this.form.getControl('correction').markAsDirty();
    if (type === '+' && +currentCorrection < maxCorrection) {
      const nextValue = currentCorrection + increment;
      setControlValue(this.form.group, 'correction', parseFloat(nextValue.toFixed(2)));
      return;
    }
    if (type === '-' && currentCorrection > maxCorrection * (-1)) {
      const nextValue = currentCorrection - increment;
      setControlValue(this.form.group, 'correction', parseFloat(nextValue.toFixed(2)));
    }
  }

// tslint:disable-next-line:max-line-length
  protected buildForm(snapshot: FormStateSnapshot<DrawDetailsPaymentProgressInfo>): FormState<DrawDetailsPaymentProgressInfo> {
    return this.formStateBuilder.buildPlain({
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      snapshot,
      ...this.getFormSchemaConfig(getFormStateSnapshotValue(snapshot)),
    });
  }

  protected afterRestored(snapshot: FormStateSnapshot<DrawDetailsPaymentProgressInfo>): void {
    updateSchemaWithChildren(this.form.group, this.getFormSchemaConfig(getFormStateSnapshotValue(snapshot)));
  }

  protected afterChanges(): void {
    updateSchemaWithChildren(this.form.group, this.getFormSchemaConfig(this.form.submitValue));
  }

  private getFormSchemaConfig(value: DrawDetailsPaymentProgressInfo): FormGroupSchemaConfig {
    return {
      children: {
        invoiceNumber: { enabled: false },
        totalDue: { enabled: false },
        invoicedToDate: { enabled: false },

        thisDraw: { required: true, type: NumberTypes.SignedPrice },
        thisDrawGstAmount: { visible: whenAuOrNzRegion(), enabled: false },
        thisDrawAmountWithGst: { visible: whenAuOrNzRegion(), enabled: false },
        correction: {
          visible: whenAuOrNzRegion(),
          // tslint:disable-next-line:no-magic-numbers
          validators: [Validators.min(-0.1), Validators.max(0.1)],
        },

        recommendedDraw: { enabled: false },
        recommendedDrawGstAmount: { visible: whenAuOrNzRegion(), enabled: false },
        recommendedDrawAmountWithGst: { visible: whenAuOrNzRegion(), enabled: false },

        memo: void 0,
        historyAreaMemo: void 0,

        taxRate: {
          visible: [whenAuOrNzRegion(), value.gstApplies],
          enabled: value.gstApplies,
        },
        gstApplies: void 0,
      },
    };
  }
}
